//src/pages/internal/cadastroHosts/dataTable/index.js
import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { HotTable } from "@handsontable/react";
import { registerAllModules } from "handsontable/registry";
import "handsontable/dist/handsontable.full.min.css";
import { alpha, Box, Paper, useTheme, Snackbar, Alert } from "@mui/material";
import "handsontable/styles/ht-theme-main.css";
import { useNavigate } from "react-router-dom";

registerAllModules();

// --- Ícones SVG ---
const EDIT_ICON_SVG = `<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>`;
const DELETE_ICON_SVG = `<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M16 9v10H8V9h8m-1.5-6h-5l-1 1H5v2h14V4h-3.5l-1-1zM18 7H6v12c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7z"/></svg>`;

// --- CSS Mantido (Igual ao original) ---
const customStyles = (theme) => `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');

  :root {
    --bg-app: #f9f1f2;
    --bg-card: #ffffff;
    --text-primary: #334155;
    --text-secondary: #64748b;
    --border-subtle: #f0e2e7;
    --row-odd: #ffffff;
    --row-even: #f8fafc; 
    --row-hover: #f1f5f9;
    --header-bg: #ffffff;
    --header-text: #64748b;
    --border-header: #ff0000;
    --input-bg: #ffffff;
    --error-text: #ef4444;
    --header-active-bg: ${alpha(theme.palette.primary.main, 0.1)}; 
    --header-active-text: ${theme.palette.primary.main};
    
    --action-edit: ${
			theme.palette.mode === "dark"
				? theme.palette.text.main
				: theme.palette.colors.gray.medium
		};
    --action-delete: ${theme.palette.error.main};
    --action-hover-bg: ${alpha(theme.palette.action.hover, 0.1)};
  }

  body.dark-theme {
    --bg-app: #0f172a;
    --bg-card: #1e293b;
    --text-primary: #e2e8f0;
    --text-secondary: #94a3b8;
    --border-subtle: ${
			theme.palette.mode === "dark"
				? theme.palette.secondary.dark
				: theme.palette.secondary.lighter
		};
    --row-odd: ${
			theme.palette.mode == "dark"
				? theme.palette.secondary.dark
				: theme.palette.secondary.lighter
		};
    --row-even: ${theme.palette.background.paper};
    --row-hover: ${alpha(theme.palette.background.paper, 0.8)};
    --header-bg: ${theme.palette.background.paper};
    --header-text: ${
			theme.palette.mode == "dark"
				? theme.palette.secondary.light
				: theme.palette.secondary.dark
		};
    --input-bg: #1e293b;
    --error-text: #4f2626db;
    --header-active-bg: ${theme.palette.background.paper};
    --header-active-text: ${theme.palette.primary.main};
    --action-edit: ${theme.palette.text.main};
    --action-delete: ${theme.palette.error.main};
  }

  .custom-table-wrapper { font-family: 'Inter', sans-serif; transition: background-color 0.3s ease; }
  .custom-table-wrapper .handsontable { border: none !important; overflow: hidden; }
  .ht_master .wtHider { border: none !important; color: var(--text-primary) !important; }

  .custom-table-wrapper .handsontable th {
    background-color: var(--header-bg);
    color: var(--header-text);
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 2px solid var(--border-subtle);
    border-top: none; border-right: none !important; border-left: none !important;
    padding: 0px 5px; text-align: left; vertical-align: middle;
  }

  .custom-table-wrapper .handsontable td {
    color: var(--text-primary);
    font-size: 12px;
    border-bottom: 1px solid var(--border-subtle);
    border-right: none !important; border-left: none !important;
    padding: 1px 5px; vertical-align: middle;
    transition: background-color 0.15s ease;
  }

  .error-cell { color: var(--error-text) !important; font-weight: 500; }

  .custom-table-wrapper .handsontable tbody tr:nth-of-type(odd) td,
  .custom-table-wrapper .handsontable tbody tr:nth-of-type(odd) th { background-color: var(--row-odd); }
  .custom-table-wrapper .handsontable tbody tr:nth-of-type(even) td,
  .custom-table-wrapper .handsontable tbody tr:nth-of-type(even) th { background-color: var(--row-even); }
  .custom-table-wrapper .handsontable tbody tr:hover td,
  .custom-table-wrapper .handsontable tbody tr:hover th { background-color: var(--row-hover) !important; }

  .htDropdownMenu, .htAutocomplete { background: var(--bg-card); padding: 4px; border: 1px solid var(--border-subtle); }
  .htDropdownMenu table.htCore td, .htAutocomplete table.htCore td {
     background-color: var(--bg-card) !important; color: var(--text-primary) !important; border: none; border-radius: 4px; padding: 8px 12px;
  }
  
  .handsontableInput { background-color: var(--input-bg) !important; color: var(--text-primary) !important; font-family: 'Inter', sans-serif !important; font-size: 13px !important; }
  .handsontableInputHolder { background-color: var(--input-bg) !important; }

  /* Chips */
  .ht-chip {
    display: inline-flex; 
    align-items: center; 
    justify-content: center; height: 20px;
    padding: 0 10px; 
    border-radius: 4px; 
    font-size: 0.55rem; 
    font-weight: 900; 
    cursor: pointer; border: 1px solid transparent; 
    transition: all 0.2s;
  }
  .ht-chip-success { 
  background-color: ${theme.palette.chip.background.success}; 
    color: ${theme.palette.chip.colorText.success}; 
    border-color: ${theme.palette.chip.colorText.success};
    border-radius: 10px;
    }
  .ht-chip-error { 
    background-color: ${theme.palette.chip.background.critical}; 
    color: ${theme.palette.chip.colorText.critical}; 
    border: "0.1px solid ";
    border-color: ${theme.palette.chip.colorText.critical};
    border-radius: 10px;
  }
  .ht-chip-null { 
    background-color: ${theme.palette.action.hover}; 
    color: var(--text-secondary); 
    border-color: var(--text-secondary); 
    border-radius: 10px;
    opacity: 0.7; 
  }
  .ht-chip-null:hover { opacity: 1; border-color: var(--text-secondary); }

  .action-cell-container {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;
      height: 100%;
      width: 100%;
  }

  .action-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 4px;
      border-radius: 50%;
      transition: background-color 0.2s;
  }
  .action-btn:hover { background-color: rgba(0,0,0,0.05); }
  .action-btn.edit { color: var(--action-edit); }
  .action-btn.delete { color: var(--action-delete); }

  .custom-table-wrapper .handsontable th.ht__highlight {
    background-color: var(--header-active-bg) !important;
    color: var(--header-active-text) !important;
    border-bottom: 2px solid var(--header-active-text);
    transition: background-color 0.2s ease;
  }

  .custom-table-wrapper.view-mode .htAutocompleteArrow { display: none !important; }
  .custom-table-wrapper.view-mode .htAutocomplete { padding-right: 5px !important; }
  .custom-table-wrapper input[type="checkbox"] {
    appearance: none;
    -webkit-appearance: none;
    width: 14px;
    height: 14px;
    border: 2px solid;
    border-radius: 3px;
    background-color: transparent;
    cursor: pointer;
    position: relative;
    border-color: ${theme.palette.text.main}80;
}
.custom-table-wrapper input[type="checkbox"]:hover {
  border-color: var(--header-active-text);
  background-color: ${alpha(theme.palette.primary.main, 0.08)};
}
  .custom-table-wrapper input[type="checkbox"]:checked {
  background-color: var(--header-active-text);
  border-color: var(--header-active-text);
}
.custom-table-wrapper input[type="checkbox"]:checked::after {
  content: "";
  position: absolute;
  top: 1px;
  left: 4px;
  width: 4px;
  height: 8px;
  border: solid ${theme.palette.background.paper};
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.custom-table-wrapper .handsontable,
.custom-table-wrapper .handsontable thead,
.custom-table-wrapper .handsontable thead tr:first-child th {
  border-top: none !important;
}

/* Tooltip flutuante de associações de clientes/serviços */
.assoc-tooltip {
  display: none;
  position: fixed;
  z-index: 2000;
  max-height: 220px;
  overflow-y: auto;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 6px 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.18);
  pointer-events: auto;
}
.assoc-tooltip-row {
  font-size: 11px;
  color: var(--text-primary);
  padding: 6px 4px;
  white-space: nowrap;
  border-bottom: 1px solid var(--border-subtle);
  cursor: pointer;
  transition: background-color 0.2s;
}
.assoc-tooltip-row:hover {
  background-color: var(--row-hover);
}
.assoc-tooltip-row:last-child {
  border-bottom: none;
}
.assoc-icon-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  cursor: pointer;
  color: var(--action-edit);
  transition: all 0.2s;
  border-radius: 4px;
}
.assoc-icon-chip:hover {
  background-color: rgba(0,0,0,0.05);
}
.assoc-icon-chip-empty {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  cursor: default;
  opacity: 0.4;
  color: var(--text-secondary);
}
.assoc-badge {
  position: absolute;
  top: -1px;
  right: -1px;
  background: var(--header-active-text);
  color: #fff;
  border-radius: 50%;
  font-size: 8px;
  font-weight: bold;
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--bg-card);
}
`;

export default function NetworkTable({
	columns,
	rows,
	statusColumn = false,
	isEditable = false,
	setIsEditable,
	equipmentMap = {},
	onEditRow,
	onDeleteRow,
}) {
	const theme = useTheme();
	const isDarkMode = theme.palette.mode === "dark";
	const hotRef = useRef(null);
	const tooltipRef = useRef(null);
	const hideTooltipTimeout = useRef(null);
	const navigate = useNavigate();

	const [updateTrigger, setUpdateTrigger] = useState(0);
	const [notification, setNotification] = useState({
		open: false,
		message: "",
		severity: "success",
	});

	// Monta o div do tooltip no body e remove ao desmontar o componente
	useEffect(() => {
		const el = document.createElement("div");
		el.className = "assoc-tooltip";

		el.addEventListener("mouseenter", () => {
			if (hideTooltipTimeout?.current) clearTimeout(hideTooltipTimeout?.current);
		});
		el.addEventListener("mouseleave", () => {
			el.style.display = "none";
		});

		el.addEventListener("click", (e) => {
			const row = e.target?.closest(".assoc-tooltip-row");
			if (!row) return;
			const clienteId = row?.dataset?.clienteId;
			const servicoId = row?.dataset?.servicoId;
			if (clienteId) {
				const clientStorage = {
					interfaceId: "",
					clienteId: clienteId,
					serviceId: servicoId,
					hostId: "",
					selectTime: 5,
					initialDate: "",
					finalDate: "",
				};
				localStorage.setItem("filterData", JSON.stringify(clientStorage));
				navigate(`/interno/home/perfil/config/${clienteId}`);
				if (el?.style) el.style.display = "none";
			}
		});

		document.body.appendChild(el);
		tooltipRef.current = el;
		return () => el?.remove();
	}, [navigate]);

	useEffect(() => {
		if (isDarkMode) {
			document.body.classList.add("dark-theme");
			document.body.style.backgroundColor = "#0f172a";
		} else {
			document.body.classList.remove("dark-theme");
			document.body.style.backgroundColor = "#f1f5f9";
		}
	}, [isDarkMode]);

	// --- RENDERIZADOR DE AÇÕES ---
	const actionsRenderer = (
		instance,
		td,
		row,
		col,
		prop,
		value,
		cellProperties,
	) => {
		while (td.firstChild) {
			td.removeChild(td.firstChild);
		}

		const container = document.createElement("div");
		container.className = "action-cell-container";

		const associations = instance?.getSourceDataAtRow?.(row)?.associations ?? [];
		const count = new Set(associations.map((a) => a?.clienteId)).size;

		const assocContainer = document.createElement("div");
		const linkIconSvg = `<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>`;

		if (count > 0) {
			assocContainer.className = "assoc-icon-chip";
			assocContainer.dataset.associations = JSON.stringify(associations);
			assocContainer.innerHTML = `${linkIconSvg}<span class="assoc-badge">${count}</span>`;
		} else {
			assocContainer.className = "assoc-icon-chip-empty";
			assocContainer.innerHTML = linkIconSvg;
		}

		const editBtn = document.createElement("div");
		editBtn.className = "action-btn edit";
		editBtn.innerHTML = EDIT_ICON_SVG;
		editBtn.dataset.action = "edit";
		editBtn.title = "Editar";

		const deleteBtn = document.createElement("div");
		deleteBtn.className = "action-btn delete";
		deleteBtn.innerHTML = DELETE_ICON_SVG;
		deleteBtn.dataset.action = "delete";
		deleteBtn.title = "Excluir";

		container.appendChild(assocContainer);
		container.appendChild(editBtn);
		container.appendChild(deleteBtn);
		td.appendChild(container);
		td.style.textAlign = "center";
		td.style.verticalAlign = "middle";
	};

	// --- RENDERIZADOR DE CHIPS UNIFICADO (PING OU SNMP) ---
	// Agora renderiza apenas UM chip baseado na propriedade da coluna
	const chipRenderer = (
		instance,
		td,
		row,
		col,
		prop,
		value,
		cellProperties,
	) => {
		while (td.firstChild) {
			td.removeChild(td.firstChild);
		}

		// Determina o Label baseado no 'prop' (checks.ping ou checks.snmp)
		let label = "UNK";
		if (prop === "checks.ping") label = "PING";
		if (prop === "checks.snmp") label = "SNMP";

		const span = document.createElement("span");
		let chipClass = "ht-chip-null";

		if (value === true) {
			chipClass = "ht-chip-success";
		} else if (value === false) {
			chipClass = "ht-chip-error";
		}

		span.className = `ht-chip ${chipClass}`;
		span.innerText = label;
		// Importante: data-prop ajuda a identificar qual campo editar no clique
		span.dataset.action = "toggle-check";
		span.dataset.prop = prop;

		td.appendChild(span);
		td.className = "chip-cell";
		td.style.textAlign = "center";
		td.style.verticalAlign = "middle";
		// td.style.padding = "4px"; // Ajuste fino para o chip preencher
	};

	// --- MANIPULADOR DE CLIQUES ---
	const handleBeforeOnCellMouseDown = (event, coords, TD) => {
		const hotInstance = hotRef.current.hotInstance;

		// Se clicou no header
		if (coords.row < 0) {
			if (coords.col === 0) {
				const currentData = hotInstance.getSourceData();
				const newState = !currentData.every((row) => row.selected);
				currentData.forEach((row, index) =>
					hotInstance.setDataAtRowProp(index, "selected", newState),
				);
				setUpdateTrigger((prev) => prev + 1);
				event.stopImmediatePropagation();
				event.preventDefault();
				return false;
			}
			return;
		}

		// Se clicou em uma célula normal
		if (coords.row >= 0) {
			const prop = hotInstance.colToProp(coords.col);

			// Lógica Checks (Ping/SNMP separados)
			// Agora verifica se a propriedade é checks.ping OU checks.snmp
			if (prop === "checks.ping" || prop === "checks.snmp") {
				if (isEditable === false) return;

				const target = event.target;
				// Verifica se clicou no chip ou na célula contendo o chip
				if (
					target &&
					(target.dataset.action === "toggle-check" ||
						target.classList.contains("chip-cell"))
				) {
					// Pega o valor atual direto daquela propriedade (true/false/null)
					const currentVal = hotInstance.getDataAtRowProp(coords.row, prop);

					// Inverte o valor
					hotInstance.setDataAtRowProp(coords.row, prop, !currentVal);
				}
				event.stopImmediatePropagation();
				event.preventDefault();
				return false;
			}

			// --- LÓGICA DE AÇÕES (EDIT/DELETE) ---
			if (prop === "actions") {
				const target = event.target.closest(".action-btn");
				if (target) {
					const action = target.dataset.action;
					const rowData = hotInstance.getSourceDataAtRow(coords.row);
					if (action === "edit") {
						if (onEditRow) onEditRow(rowData);
					} else if (action === "delete") {
						if (onDeleteRow) onDeleteRow(rowData);
					}
				}
				event.stopImmediatePropagation();
				event.preventDefault();
				return false;
			}
		}
	};

	const getColHeaders = useCallback(
		(col) => {
			const headers = [
				"ID",
				"Host",
				"IP",
				"Ping",
				"SNMP",
				"Comunidade",
				"Porta SNMP",
				"Fabricante",
				"Modelo",
				"User SSH",
				"Senha SSH",
				"Porta SSH",
				"Proxy",
				...(statusColumn ? ["Status"] : []),
				"Ações",
			];

			if (statusColumn && col >= headers.length) return "";
			return headers[col];
		},
		[statusColumn],
	);

	const cellsConfig = useCallback(
		(row, col, prop) => {
			const cellProperties = {};
			const hotInstance = hotRef.current?.hotInstance;

			// Renderer de Ações
			if (prop === "actions") {
				cellProperties.readOnly = true;
				cellProperties.renderer = actionsRenderer;
				return cellProperties;
			}


			// Renderer de Chips (Ping ou SNMP)
			if (prop === "checks.ping" || prop === "checks.snmp") {
				cellProperties.readOnly = true;
				cellProperties.renderer = chipRenderer;
				return cellProperties;
			}

			if (prop === "error_msg") {
				cellProperties.readOnly = true;
				cellProperties.className = "error-cell";
				return cellProperties;
			}

			cellProperties.readOnly = !isEditable;

			if (hotInstance && prop === "modelo") {
				const fabricante = hotInstance.getDataAtRowProp(row, "fabricante");
				cellProperties.source = equipmentMap[fabricante] || [];
			}
			return cellProperties;
		},
		[isEditable, equipmentMap],
	);

	return (
		<Paper sx={{ pt: 2, borderRadius: "16px" }}>
			<style>{customStyles(theme)}</style>
			<Box style={{ margin: "0 auto" }}>
				<Box
					className={`custom-table-wrapper ${!isEditable ? "view-mode" : ""}`}
					onMouseOver={(e) => {
						// Exibe tooltip com associações ao passar o mouse sobre o ícone
						const chip = e.target?.closest?.(".assoc-icon-chip");
						const tip = tooltipRef?.current;
						if (!chip || !tip) return;
						if (hideTooltipTimeout?.current) clearTimeout(hideTooltipTimeout?.current);

						const associations = JSON.parse(chip?.dataset?.associations || "[]");
						if (!associations?.length) return;
						tip.innerHTML = associations
							?.map(
								(a) =>
									`<div class="assoc-tooltip-row" data-cliente-id="${a?.clienteId}" data-servico-id="${a?.servicoId}">
										<strong>${a?.clientName ?? "—"}</strong> — ${a?.type ?? ""} <span style="opacity:0.6">(${a?.servicoId ?? ""})</span>${
											// ERP do cliente: o número do serviço se repete entre ERPs
											a?.erp?.nome
												? ` <span style="opacity:0.6">· ${String(a.erp.nome).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`)}</span>`
												: ""
										}
									</div>`,
							)
							?.join("");
						const rect = chip?.getBoundingClientRect();
						if (tip?.style) {
							// Mostra temporariamente para medir a largura real
							tip.style.display = "block";
							const tipWidth = tip.offsetWidth;
							
							const idealLeft = Math.max(10, (rect?.left || 0) - 50);
							// Garante que não ultrapasse a margem direita da janela
							const maxLeft = window.innerWidth - tipWidth - 15;
							
							tip.style.top = `${(rect?.bottom || 0) + 4}px`;
							tip.style.left = `${Math.min(idealLeft, maxLeft)}px`;
						}
					}}
					onMouseOut={(e) => {
						// Esconde o tooltip com delay para permitir o mouse se mover até ele
						const chip = e.target?.closest?.(".assoc-icon-chip");
						if (!chip) return;
						const tip = tooltipRef?.current;
						if (tip) {
							hideTooltipTimeout.current = setTimeout(() => {
								if (tip?.style) tip.style.display = "none";
							}, 300);
						}
					}}
					sx={{
						padding: "0px",
						minHeight: "400px",
						overflow: "auto",
						position: "relative",
					}}
				>
					<HotTable
						ref={hotRef}
						data={rows}
						licenseKey="non-commercial-and-evaluation"
						rowHeaders={false}
						height="70vh"
						stretchH="all"
						rowHeights={10}
						cells={cellsConfig}
						columnSorting={true}
						afterChange={(changes, src) =>
							src !== "loadData" &&
							changes?.some(([r, p]) => p === "selected") &&
							setUpdateTrigger((p) => p + 1)
						}
						beforeOnCellMouseDown={handleBeforeOnCellMouseDown}
						colHeaders={getColHeaders}
						columns={columns}
						manualColumnResize={true}
					/>
				</Box>
			</Box>
			<Snackbar
				open={notification.open}
				autoHideDuration={6000}
				onClose={() => setNotification({ ...notification, open: false })}
			>
				<Alert
					onClose={() => setNotification({ ...notification, open: false })}
					severity={notification.severity}
					sx={{ width: "100%" }}
				>
					{notification.message}
				</Alert>
			</Snackbar>
		</Paper>
	);
}
